import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import {
    archiveChatSession as apiArchiveChatSession,
    updateChatSession as apiUpdateChatSession,
    createChatSession,
    CreateChatSessionOptions,
    getChatSessions
} from '@/services/chat_sessions';
import { useTenantStore } from '@/stores/tenant';
import { ChatSession, DataSource } from '@/types';

export const useChatSessionsStore = defineStore('chatSessions', () => {
    const chatSessions = ref<ChatSession[]>([]);
    const tenantStore = useTenantStore();
    const shouldScrollToTop = ref(false);
    const skill = ref<string>('');
    const documentToReload = ref<boolean>(false);
    const isUploading = ref<boolean>(false);
    const firstVisit = ref<boolean>(true);
    const isLoadingChatSession = ref<boolean>(true);
    const currentMessage = ref<string>('');
    const isSettingUpSession = ref<boolean>(false);
    const isComingFromNewChat = ref<boolean>(false);
    const pendingDataSources = ref<DataSource[]>([]);
    const isRedirectingToNewChat = ref<boolean>(false);

    const pendingMessages = ref<string[]>([]);
    const addPendingMessage = (message: string) => {
        pendingMessages.value.push(message);
    };
    const removePendingMessage = (message: string) => {
        pendingMessages.value = pendingMessages.value.filter(
            (m) => m !== message
        );
    };

    const showChatUX = computed(() => {
        return !firstVisit.value && !isLoadingChatSession.value;
    });

    const agentVersion = ref<number | null>(null);

    async function fetchChatSessions(tenantId: string) {
        chatSessions.value = await getChatSessions(tenantId);
    }

    async function addChatSession(data: CreateChatSessionOptions) {
        const newChat = await createChatSession(data);
        chatSessions.value.unshift(newChat);
        isComingFromNewChat.value = true;
        return newChat;
    }

    async function archiveChatSession(chatSessionId: string, tenantId: string) {
        await apiArchiveChatSession(chatSessionId, tenantId);
        chatSessions.value = chatSessions.value.filter(
            (cs) => cs.id !== chatSessionId
        );
    }

    function moveChatSessionToTop(chatSessionId: string) {
        const index = chatSessions.value.findIndex(
            (cs) => cs.id === chatSessionId
        );
        if (index !== -1) {
            const [chatSession] = chatSessions.value.splice(index, 1);
            chatSessions.value.unshift(chatSession);
            shouldScrollToTop.value = true;
        }
    }

    async function markChatSessionAsVisited(
        chatSessionId: string
    ): Promise<ChatSession | undefined> {
        if (!tenantStore.tenantId) {
            return;
        }

        const chatSession = await apiUpdateChatSession({
            chatSessionId: chatSessionId,
            tenantId: tenantStore.tenantId,
            firstVisit: false
        });
        if (chatSession) {
            firstVisit.value = chatSession.firstVisit;
        }
    }

    /**
     * `projectId` moves the session to another project; `''` detaches it, which is what the API
     * reads as "no project". Passing `null` here means the same thing, since that is what the
     * sidebar's own copy holds for a session belonging to none.
     */
    async function updateChatSession(
        chatSessionId: string,
        name?: string,
        documentId?: string,
        customAgentId?: string,
        projectId?: string | null
    ) {
        if (!tenantStore.tenantId) {
            return;
        }

        await apiUpdateChatSession({
            chatSessionId: chatSessionId,
            tenantId: tenantStore.tenantId,
            name: name,
            documentId: documentId,
            customAgentId: customAgentId,
            projectId: projectId === undefined ? undefined : (projectId ?? '')
        });
        const chatSession = chatSessions.value.find(
            (cs) => cs.id.toString() === chatSessionId.toString()
        );
        if (chatSession) {
            if (name !== undefined) {
                chatSession.name = name;
            }
            if (documentId !== undefined) {
                chatSession.document_id = documentId;
            }
            if (customAgentId !== undefined) {
                chatSession.custom_agent_id = customAgentId;
            }
            if (projectId !== undefined) {
                chatSession.project_id = projectId;
            }
        }
    }

    function updateChatSessionDocumentId(
        chatSessionId: string,
        documentId: string
    ) {
        const chatSession = chatSessions.value.find(
            (cs) => cs.id.toString() === chatSessionId.toString()
        );
        if (chatSession) {
            chatSession.document_id = documentId;
        }
    }

    return {
        chatSessions,
        shouldScrollToTop,
        skill,
        documentToReload,
        isUploading,
        fetchChatSessions,
        addChatSession,
        archiveChatSession,
        moveChatSessionToTop,
        updateChatSession,
        updateChatSessionDocumentId,
        markChatSessionAsVisited,
        firstVisit,
        showChatUX,
        isLoadingChatSession,
        pendingMessages,
        addPendingMessage,
        removePendingMessage,
        currentMessage,
        isSettingUpSession,
        isComingFromNewChat,
        pendingDataSources,
        isRedirectingToNewChat,
        agentVersion
    };
});
